<template>
    <div class="timer-product-kpi-cards" role="group" :aria-label="$t('page_title.TimersProduct')">
        <button
            v-for="card in cards"
            :key="card.type"
            type="button"
            class="timer-product-kpi-card"
            :class="[card.type, card.severity, { active: activeKpiFilter === card.type, 'has-leading-icon': showLeadingIcon }]"
            @click="handleKpiClick(card.type)"
        >
            <span v-if="showLeadingIcon" class="timer-product-kpi-card__icon" aria-hidden="true">
                <i class="i-andy-verify i-andy-m"></i>
            </span>
            <span class="timer-product-kpi-card__body">
                <span class="timer-product-kpi-card__label">{{ card.label }}</span>
                <span class="timer-product-kpi-card__footer">
                    <span class="timer-product-kpi-card__value">
                        <span class="timer-product-kpi-card__main">{{ card.value }}</span><span class="timer-product-kpi-card__total">/{{ card.total }}</span>
                    </span>
                    <i v-if="showExternalLink" class="i-andy-external timer-product-kpi-card__link" aria-hidden="true"></i>
                    <span v-else class="timer-product-kpi-card__filter">{{ $t('timersproduct.kpi.filter') }}</span>
                </span>
            </span>
        </button>
    </div>
</template>

<script>
/**
 * KPI Cards de Product Timers.
 * UI (BUILD-7483) + contadores/filtro 100% reactivos vía Vuex (BUILD-7489).
 */
const KPI_TYPES = ['with_timer', 'without_timer']

/** Umbral local: sin timer en rojo solo si el contador es > 0. */
const WITHOUT_TIMER_CRITICAL_THRESHOLD = 0

export default {
    name: 'TimerProductKpiCards',
    props: {
        /**
         * Vista principal: icono de enlace externo.
         * Listado de productos (false): muestra la etiqueta "Filtrar".
         */
        showExternalLink: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        /** Contadores: se recalculan solos al mutar products/timers en la store. */
        kpiCounts() {
            return this.$store.getters['timerproduct/getProductTimerKpiCounts']
        },
        /** Filtro activo compartido entre board y listado. */
        activeKpiFilter() {
            return this.$store.getters['timerproduct/getActiveKpiFilter']
        },
        /**
         * Iconos laterales del diseño Figma: visibles fuera de portrait (móvil).
         */
        showLeadingIcon() {
            return !['portrait'].includes(this.$mq)
        },
        cards() {
            const total = this.kpiCounts.total
            const withTimer = this.kpiCounts.with_timer
            const withoutTimer = this.kpiCounts.without_timer

            return [
                {
                    type: 'with_timer',
                    label: this.$t('timersproduct.kpi.with_timer'),
                    value: withTimer,
                    total,
                    severity: null
                },
                {
                    type: 'without_timer',
                    label: this.$t('timersproduct.kpi.without_timer'),
                    value: withoutTimer,
                    total,
                    severity: withoutTimer > WITHOUT_TIMER_CRITICAL_THRESHOLD ? 'critical' : null
                }
            ]
        }
    },
    methods: {
        handleKpiClick(type) {
            if (!KPI_TYPES.includes(type)) {
                return
            }

            const nextFilter = this.activeKpiFilter === type ? null : type
            this.$store.dispatch('timerproduct/setActiveKpiFilter', nextFilter)
        }
    }
}
</script>

<style lang="scss" scoped>
.timer-product-kpi-cards {
    @include display-flex();
    gap: 12px;
    margin: 12px 8px;
    width: auto;
    max-width: 100%;
    box-sizing: border-box;
    flex-wrap: nowrap;
    flex-shrink: 0;
    min-width: 0;
    overflow: visible;
    align-self: stretch;
}

.timer-product-kpi-card {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 8px;
    border: 1px solid $neutro-dark;
    border-radius: 4px;
    background: $color-white;
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;
    overflow: hidden;
    @include display-flex();
    @include flex-direction(column);
    @include align-items(stretch);
    gap: 8px;

    &.has-leading-icon {
        @include flex-direction(row);
        @include align-items(center);
        gap: 12px;
        padding: 12px;
    }

    @media (hover: hover) {
        &:hover {
            border-color: $main;
        }
    }

    &.active {
        border-color: $main;
        background: $main-t90;
    }

    &__icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 4px;
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);

        i {
            width: 29.251px;
            height: 30px;
            font-size: 30px;
            line-height: 30px;
        }
    }

    &__body {
        flex: 1 1 auto;
        min-width: 0;
        @include display-flex();
        @include flex-direction(column);
        @include align-items(stretch);
        gap: 8px;
    }

    &__label {
        @include font-size(sm);
        color: $color-black;
        font-family: Avenir-Regular;
        width: 100%;
        max-width: 100%;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
        hyphens: auto;
    }

    &__footer {
        @include display-flex();
        @include align-items(flex-end);
        @include justify-content(space-between);
        gap: 8px;
        width: 100%;
        min-width: 0;
    }

    &__value {
        display: inline-flex;
        align-items: baseline;
        min-width: 0;
    }

    &__main {
        color: $color-black;
        text-align: center;
        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        font-family: Avenir-Bold;
        position: relative;
        top: -2px;
    }

    &__total {
        color: $color-black;
        text-align: center;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        font-family: Avenir-Regular;
    }

    &__link {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        font-size: 16px;
        line-height: 16px;
        color: var(--main-dark);
    }

    &__filter {
        flex-shrink: 0;
        color: $main-text;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 16px;
        font-family: Avenir-Bold, $text-bold;
    }

    &.with_timer .timer-product-kpi-card__icon {
        background-color: $done-t90;

        i {
            color: $done;
        }
    }

    &.without_timer .timer-product-kpi-card__icon {
        background-color: $error-t90;

        i {
            color: $error-dark;
        }
    }

    &.critical .timer-product-kpi-card__main {
        color: $color-error-500;
        font-family: Avenir-Bold;
    }
}

@include media('<=679px') {
    .timer-product-kpi-cards {
        gap: 8px;
        margin: 8px;
    }
}

// En <=tablet html { font-size: 20px }, así que 1rem del mixin sm se ve a 20px.
@include media('<=tablet') {
    .timer-product-kpi-card__label {
        font-size: 14px;
        line-height: 18px;
    }
}
</style>
